/*
PURPOSE: Centralized copy for access control messaging throughout the platform

USE CASES:
- Access denied modals and messages
- Upgrade prompts for non-contributors  
- Educational content about contributor requirements
- Tab and feature explanations

DOMAIN BOUNDARIES:
- Responsible for all access control user-facing text
- Should align with Coral's brand voice: direct, visceral, human
- Must reinforce core philosophy about intention and commons

ASSUMPTIONS:
- Users understand the concept of "backing artists" 
- Copy should teach why access control exists, not just what to do
- Aligns with brand strategy of "intention over extraction"
*/

/**
 * Access Control Copy aligned with Coral's brand voice and philosophy
 * 
 * Based on brand strategy:
 * - Direct and visceral language ("give a shit" vs "care deeply")
 * - Transparent about money and value flows
 * - Emphasizes intention and explicit choice over extraction
 * - Uses three-beat messaging patterns where appropriate
 */
export const accessControlCopy = {
  // Main access denied modal
  accessDenied: {
    title: "No coral, no commons.",
    message: "Add your coral to join a community who values music.\n\nYour backing unlocks conversations across every artist and scene. Discover music through people's actual taste, not algorithms.\n\nBuilding music's commons requires contributors, not spectators.",
    cta: {
      primary: "+ ADD CORAL",
    }
  },
  
  // Coral activation state - has corals but not contributing
  coralActivation: {
    title: "Ready to make your signal visible?",
    message: "Your coral is created but not yet active. Activate it with any monthly amount to unlock community features.\n\nYour backing makes your taste visible to others and unlocks conversations across every artist and scene.\n\nEvery contribution, no matter the size, helps build music's commons.",
    cta: {
      primary: "Activate Your Coral",
    }
  },
  
  // Tab-specific messaging  
  tabs: {
    feed: {
      title: "Activity",
      restrictedMessage: "Where contributors share what moves them",
      tooltip: "See reviews, discoveries, and conversations from people backing artists"
    },
    
    commons: {
      title: "Commons", 
      restrictedMessage: "The ecosystem view",
      tooltip: "Explore trending artists, shared tracks, and community signals"
    }
  },
  
  // Educational content for Coral tab when user is non-contributor
  coralTab: {
    welcome: {
      heading: "Your corals live here",
      subheading: "This is where you build and manage your artist backing"
    },
    
    philosophy: {
      intro: "Every coral is an explicit choice—a public declaration of what deserves to exist.",
      connection: "When you back artists, you don't just support them financially. You unlock access to a community of people who make intentional choices about music.",
      commons: "Your contributions help tend the commons. Real conversations. Genuine discovery. Music thriving on intention, not extraction."
    },
    
    nextSteps: {
      heading: "Ready to make your signal visible?",
      description: "Start with any monthly amount. Add any artists. Your choices unlock community features.",
      cta: "Create Your First Coral"
    }
  },
  
  // Feature-specific messaging
  features: {
    shareMusic: {
      hiddenReason: "Share music and join conversations after you create a coral",
      encouragement: "Contributors can share discoveries and start discussions about tracks and albums"
    },
    
    crossUserProfiles: {
      restrictedMessage: "See how other contributors are backing artists",
      benefit: "Discover new music through real people's explicit choices"
    },
    
    artistPages: {
      restrictedMessage: "Full artist profiles and discussions",
      benefit: "Access complete artist info, reviews, and community conversations"
    }
  },
  
  // Brand-aligned phrases to use consistently
  brandPhrases: {
    // Core concepts
    intention: "intention made visible",
    commons: "tend the commons", 
    signal: "public signal",
    choice: "explicit choice",
    
    // Descriptive language (direct, visceral)
    community: "people who give a shit about music's future",
    discovery: "real people's actual taste",
    contrast: "intention, not extraction",
    
    // Action words  
    back: "back artists", // not "support"
    declare: "declare what matters", // not "share"
    unlock: "unlock community", // not "access features"
    tend: "tend the commons" // not "use the platform"
  }
};

/**
 * Helper function to get contextual access messaging
 * @param context - The specific UI context needing access control messaging
 * @returns Appropriate copy object for the context
 */
export const getAccessControlMessage = (context: 'modal' | 'coralTab' | 'feedTab' | 'commonsTab') => {
  switch (context) {
    case 'modal':
      return accessControlCopy.accessDenied;
    case 'coralTab':
      return accessControlCopy.coralTab;
    case 'feedTab':
      return accessControlCopy.tabs.feed;
    case 'commonsTab':
      return accessControlCopy.tabs.commons;
    default:
      return accessControlCopy.accessDenied;
  }
};